import { ColumnDef } from "@tanstack/react-table";
import classNames from "classnames";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { IoCheckmarkCircle, IoSearch } from "react-icons/io5";
import {
  LuArchive,
  LuBriefcase,
  LuMail,
  LuPlus,
  LuTags,
  LuUpload,
  LuUsers,
  LuX,
} from "react-icons/lu";
import { PiFileArrowUp } from "react-icons/pi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useDebounce } from "usehooks-ts";

import LegacyButton from "../../../components/button/Button";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { Avatar } from "../../../components/newUI/avatar";
import { Button } from "../../../components/newUI/button";
import { ListPager } from "../../../components/pagination/ListPager";
import CustomSelect from "../../../components/select/Select";
import { WarningExclaimSvg } from "../../../components/svg/WarningExclaimSvg";
import { Table } from "../../../components/table/Table";
import { getInitialsFromFullName } from "../../../constants/constants";
import { CustomerModel } from "../../../factories/customers/models/customerModel";
import { useListMemory, useRememberList } from "../../../hooks/useListMemory";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useActivateDeactivateMutation,
  useGetActiveCustomersQuery,
  useGetOrganizationTypesQuery,
  useImportContactsMutation,
  useImportOrganizationsMutation,
} from "../../../slices/ContactsSlice";
import {
  useAddMultipleTagMutation,
  useGetTagsQuery,
} from "../../../slices/ContactTagsSlice";
import { useGetUsersQuery } from "../../../slices/UserSlice";
import { FormSection } from "../../../support/FormSection";
import { SelectOptionType } from "../../../types/types";
import { exportDataToExcel } from "../../../utils/ExportData";
import { QBO_LOGO_URL } from "../../../utils/qbo";
import { BulkClientTeamModal } from "../../clientTeam/BulkClientTeamModal";
import { ME, resolvePeople } from "../../clientTeam/clientTeam.utils";
import { ClientTeamInfoButton } from "../../clientTeam/ClientTeamParts";
import { AddWorkModal } from "../../works/modals/AddWork.modal";
import {
  ClientEmailLink,
  useClientEmailCompose,
} from "../clientEmail/useClientEmailCompose";
import {
  ContactImportResult,
  importErrorMessage,
  importFileProblem,
  importResultMessage,
} from "../import/contactImport.utils";
import { AddContactModal } from "../modals/addContact/AddContactModal";
import { ComposeEmailModal } from "../modals/ComposeEmailModal";
import ImportContacts from "../modals/ImportContacts";
import {
  contactsCountLabel,
  listDisplayName,
} from "../utils/contactsList.utils";
import {
  ContactsListMemory,
  restoreContactsList,
} from "../utils/contactsListMemory";
import { navigateToContact } from "../utils/navigateToContacts";

export type Filters = {
  tagsValues: SelectOptionType[];
  organizationTypes: SelectOptionType[];
  yearEnd: SelectOptionType[];
};

type Params = {
  sortBy: string;
  sortOrder: string;
  page: string;
};

type ContactData = {
  Contact: string;
  "Contact Type": string;
  "Open Work": number;
  [key: string]: string | number;
};

type ImportVariant = "individual" | "organization";

type PendingImport = {
  file: File;
  variant: ImportVariant;
};

const INDIVIDUAL_TYPE_ID = "00000000-0000-0000-0000-000000001846";

const YEAR_END_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const buildExportRow = (customer: CustomerModel): ContactData => {
  const phoneNumbers = customer.phoneNumber
    ? customer.phoneNumber.split(",")
    : [];
  const emails = customer.email ? customer.email.split(",") : [];

  const phoneColumns = phoneNumbers.reduce<Record<string, string>>(
    (acc, phone, index) => ({
      ...acc,
      [`Phone Number ${index + 1}`]: phone || "",
    }),
    {},
  );

  const emailColumns = emails.reduce<Record<string, string>>(
    (acc, email, index) => ({
      ...acc,
      [`Email Address ${index + 1}`]: email || "",
    }),
    {},
  );

  return {
    Contact: customer.customerName,
    ...phoneColumns,
    ...emailColumns,
    "Contact Type": customer.customerType,
    "Open Work": customer.openWorkCounts,
  };
};

export const ActiveContacts: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const linkedFirm = getLinkedFirm();
  const orgId = linkedFirm?.orgId || "";

  const sortBy = searchParams.get("sortBy") || "Name";
  const sortOrder = (searchParams.get("sortOrder") as "asc" | "desc") || "asc";
  const page = Number(searchParams.get("page") || 1);

  const user = useAppSelector((state) => state.appReducer.user);

  // Search and filters survive opening a client and coming Back (1851).
  const memory = useListMemory<ContactsListMemory>({ scope: orgId });
  const [initial] = useState(() => restoreContactsList(memory.restored));

  const [isOpen, setIsOpen] = useState(false);
  const [isAddWorkOpen, setIsAddWorkOpen] = useState(false);
  const [isOpenCompose, setIsOpenCompose] = useState(false);
  const { openCompose, composeModal } = useClientEmailCompose();
  const [isOpenImport, setIsOpenImport] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerModel[]>(
    [],
  );
  const [search, setSearch] = useState(initial.search);
  const [tagValue, setTagValue] = useState("");
  const [organizationTypeValue, setOrganizationTypeValue] = useState("");
  const [yearEndValue, setYearEndValue] = useState("");
  const [addTag, setAddTag] = useState(false);
  const [tagsValue, setTagsValue] = useState<SelectOptionType[]>([]);
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(
    null,
  );
  const [isImportSuccessful, setIsImportSuccessful] = useState(false);
  const [importResult, setImportResult] = useState<ReturnType<
    typeof importResultMessage
  > | null>(null);
  const [isBulkArchiveConfirmOpen, setIsBulkArchiveConfirmOpen] =
    useState(false);

  const [filters, setFilters] = useState<Filters>(initial.filters);
  // Client team filters (Mg-231): people ids or "me".
  const [teamOwners, setTeamOwners] = useState<string[]>(
    initial.team?.owners ?? [],
  );
  const [teamManagers, setTeamManagers] = useState<string[]>(
    initial.team?.managers ?? [],
  );
  const [isBulkTeamOpen, setIsBulkTeamOpen] = useState(false);
  const { data: firmUsers } = useGetUsersQuery(orgId, { skip: !orgId });
  const peopleOptions = useMemo<SelectOptionType[]>(
    () => [
      { value: ME, label: "Me" },
      ...[...(firmUsers ?? [])]
        .map((u) => ({
          value: u.id,
          label:
            `${u.userProfile?.firstName || ""} ${
              u.userProfile?.lastName || ""
            }`.trim() ||
            u.userProfile?.email ||
            "Unknown person",
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [firmUsers],
  );
  const optionsFor = (values: string[]) =>
    values
      .map((v) => peopleOptions.find((o) => o.value === v))
      .filter((o): o is SelectOptionType => !!o);

  const debounceSearch = useDebounce(search, 200);

  const { data: tags, isFetching: isFetchingTags } = useGetTagsQuery(
    { orgId },
    {
      skip: !orgId,
    },
  );
  const {
    data: organizationTypeList,
    isFetching: isFetchingOrganizationTypes,
  } = useGetOrganizationTypesQuery();
  // People have no organization type; the server knows this fixed id as
  // "Individual" (Roadmap 1846, backend db-scripts/Mg-221).
  const organizationTypeOptions = useMemo<SelectOptionType[]>(
    () => [
      { value: INDIVIDUAL_TYPE_ID, label: "Individual" },
      ...(organizationTypeList || []),
    ],
    [organizationTypeList],
  );

  const [addMultipleTags] = useAddMultipleTagMutation();

  const [importContacts, { isLoading: isLoadingImportContacts }] =
    useImportContactsMutation();

  const [importOrganizations, { isLoading: isLoadingImportOrganizations }] =
    useImportOrganizationsMutation();

  const isImporting = isLoadingImportContacts || isLoadingImportOrganizations;

  const [deactivateContacts, { isLoading: isArchivingContacts }] =
    useActivateDeactivateMutation();

  const { data: contacts, isLoading: isLoadingContacts } =
    useGetActiveCustomersQuery(
      {
        orgId,
        isActive: true,
        userId: user?.id || "",
        tagIds: filters.tagsValues.map((tag) => tag.value),
        yearEndMonth: filters.yearEnd.map((month) => month.value),
        customerName: null,
        sortOrder,
        sortColumn: sortBy,
        searchQuery: debounceSearch || "",
        pageSize: 20,
        pageNumber: page,
        organizationTypeIds: filters.organizationTypes.map(
          (organizationType) => organizationType.value,
        ),
        clientOwnerUserIds: resolvePeople(teamOwners, user?.id),
        clientManagerUserIds: resolvePeople(teamManagers, user?.id),
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !orgId || !user?.id,
      },
    );

  const remembered = useMemo<ContactsListMemory>(
    () => ({
      search,
      filters,
      ...(teamOwners.length || teamManagers.length
        ? { team: { owners: teamOwners, managers: teamManagers } }
        : {}),
    }),
    [search, filters, teamOwners, teamManagers],
  );
  useRememberList(memory, remembered, !isLoadingContacts && !!contacts);

  useEffect(() => {
    if (!contacts?.length) {
      setSelectedCustomers([]);
      return;
    }

    setSelectedCustomers((prev) =>
      prev.filter((selected) =>
        contacts.some((contact) => contact.customerId === selected.customerId),
      ),
    );
  }, [contacts]);

  const yearEndOptions = useMemo<SelectOptionType[]>(() => {
    return YEAR_END_MONTHS.map((month) => ({
      value: month,
      label: month,
    }));
  }, []);

  const tagOptions = useMemo<SelectOptionType[]>(() => {
    return (tags ?? []).map((tag) => ({
      value: tag.id,
      label: tag.tagName,
    }));
  }, [tags]);

  const exportData = useMemo<ContactData[]>(() => {
    const source = selectedCustomers.length
      ? selectedCustomers
      : contacts || [];
    return source.map(buildExportRow);
  }, [selectedCustomers, contacts]);

  const selectedCustomerOptions = useMemo<SelectOptionType[]>(
    () =>
      selectedCustomers.map((customer) => ({
        value: customer.customerId || "",
        label: customer.customerName,
        avatar: getInitialsFromFullName(customer.customerName),
        avatarColor: customer.businessContactId ? "purple" : "blue",
        type: customer.businessContactId ? "purple" : "blue",
        contactId: customer.contactId || undefined,
        businessContactId: customer.businessContactId || undefined,
        customerId: customer.customerId || undefined,
      })),
    [selectedCustomers],
  );

  const hasSelectedCustomers = selectedCustomers.length > 0;
  const selectedCustomersLabel = `${selectedCustomers.length} selected`;

  const allSelected = useMemo(() => {
    if (!contacts?.length) return false;

    return contacts.every((contact) =>
      selectedCustomers.some(
        (selected) => selected.customerId === contact.customerId,
      ),
    );
  }, [contacts, selectedCustomers]);

  const handleArchiveSelectedContacts = async () => {
    if (!selectedCustomers.length) {
      return;
    }

    await deactivateContacts({
      activateCustomer: false,
      body: selectedCustomers.map((item) => item.customerId),
    });
    setSelectedCustomers([]);
    setIsBulkArchiveConfirmOpen(false);
  };

  const setParams = useCallback(
    (params: Params) => {
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const setPageParam = (nextPage: number) => {
    setParams({
      sortBy,
      sortOrder,
      page: String(nextPage),
    });
  };

  const onClickSort = useCallback(
    (column: string) => {
      if (sortBy === column) {
        setParams({
          sortBy: column,
          sortOrder: sortOrder === "asc" ? "desc" : "asc",
          page: "1",
        });
        return;
      }

      setParams({
        sortBy: column,
        sortOrder: "asc",
        page: "1",
      });
    },
    [setParams, sortBy, sortOrder],
  );

  const handleImport = (file: File, variant: ImportVariant) => {
    if (!file || isImporting) return;
    const fileProblem = importFileProblem(file.name);
    if (fileProblem) {
      toast.error(fileProblem);
      return;
    }

    setPendingImport({
      file,
      variant,
    });

    setIsOpenImport(false);
  };

  const handleCancelImport = () => {
    if (isImporting) return;

    setPendingImport(null);
  };

  const handleCloseImportSuccess = () => {
    setIsImportSuccessful(false);
    setPendingImport(null);
    setIsOpenImport(false);
  };

  const handleConfirmImport = async () => {
    if (!pendingImport || isImporting) return;

    const formData = new FormData();
    formData.append("formFile", pendingImport.file);

    const locationId = user?.org?.locations?.[0]?.id || "";

    try {
      const result =
        pendingImport.variant === "individual"
          ? await importContacts({
              body: formData,
              locationId,
            }).unwrap()
          : await importOrganizations({
              body: formData,
              locationId,
            }).unwrap();

      // QA batch 15: say how many rows were imported and which were skipped.
      setImportResult(
        importResultMessage(
          result as ContactImportResult,
          pendingImport.variant,
        ),
      );
      setPendingImport(null);
      setIsOpenImport(false);
      setIsImportSuccessful(true);
    } catch (error) {
      console.error(error);
      setPendingImport(null);
      toast.error(importErrorMessage(error));
    }
  };

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!contacts?.length) {
        setSelectedCustomers([]);
        return;
      }

      setSelectedCustomers(checked ? contacts : []);
    },
    [contacts],
  );

  const handleToggleCustomer = useCallback(
    (customer: CustomerModel, checked: boolean) => {
      if (!checked) {
        setSelectedCustomers((prev) =>
          prev.filter((item) => item.customerId !== customer.customerId),
        );
        return;
      }

      setSelectedCustomers((prev) => {
        if (prev.some((item) => item.customerId === customer.customerId)) {
          return prev;
        }

        return [...prev, customer];
      });
    },
    [],
  );

  const renderSortableHeader = useCallback(
    (label: string, column: string) => (
      <div
        className={"flex cursor-pointer items-center gap-2"}
        onClick={() => onClickSort(column)}>
        <div
          className={classNames(
            "whitespace-nowrap",
            sortBy === column && "text-[#7C66F0]",
          )}>
          {label}
        </div>
        {sortBy === column &&
          (sortOrder === "asc" ? (
            <FaArrowUp size={8} color={"#7C66F0"} />
          ) : (
            <FaArrowDown size={8} color={"#7C66F0"} />
          ))}
      </div>
    ),
    [onClickSort, sortBy, sortOrder],
  );

  const contactColumns = useMemo<ColumnDef<CustomerModel, unknown>[]>(
    () => [
      {
        id: "select",
        enableSorting: false,
        header: () => (
          <input
            className={
              "checkbox scale-75 items-center rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
            checked={allSelected}
            type={"checkbox"}
          />
        ),
        cell: ({ row }) => (
          <input
            className={
              "checkbox mt-1 scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
            }
            onChange={(e) =>
              handleToggleCustomer(row.original, e.target.checked)
            }
            type={"checkbox"}
            checked={selectedCustomers.some(
              (selected) => selected.customerId === row.original.customerId,
            )}
          />
        ),
        meta: {
          headerClassName:
            "items-center px-2 pt-2 text-start text-sm normal-case text-gray-400",
          className: "w-[30px] pl-2",
        },
      },
      {
        id: "contact",
        enableSorting: false,
        header: () => renderSortableHeader("Contact", "Name"),
        cell: ({ row }) => {
          const item = row.original;

          return (
            <div className={"flex min-w-0 items-center gap-3 text-sm"}>
              <div className={"shrink-0"}>
                <Avatar
                  id={item.customerId || item.id || item.customerName}
                  color={item.businessContactId ? "secondary" : "primary"}
                  size={"sm"}
                  label={getInitialsFromFullName(item.customerName)}
                  fullName={item.customerName}
                />
              </div>

              <div className={"flex min-w-0 flex-1 items-center gap-1"}>
                <div
                  onClick={() =>
                    navigate(
                      navigateToContact({
                        type: item.businessContactId
                          ? "organization"
                          : "contact",
                        id: item.businessContactId || item.contactId || "",
                        customerId: item.customerId || "",
                      }),
                    )
                  }
                  className={
                    "link-primary max-w-[200px] cursor-pointer truncate text-sm font-semibold hover:underline"
                  }>
                  {item.customerName}
                </div>
                {item.isSyncedWithQBO && (
                  <img
                    src={QBO_LOGO_URL}
                    height={"18px"}
                    width={"18px"}
                    alt={"QBO synced"}
                    className={"shrink-0"}
                  />
                )}
              </div>
            </div>
          );
        },
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "min-w-0 px-2 py-2",
        },
      },
      {
        id: "displayName",
        enableSorting: false,
        header: () => renderSortableHeader("Display name", "dba"),
        cell: ({ row }) => (
          <div className={"flex items-center gap-3 text-sm"}>
            <div
              className={
                "link-primary max-w-[200px] cursor-pointer truncate text-sm font-semibold hover:underline"
              }>
              {listDisplayName(row.original)}
            </div>
          </div>
        ),
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "max-w-[200px] px-2 py-2",
        },
      },
      {
        id: "organizationType",
        enableSorting: false,
        header: () =>
          renderSortableHeader("Organization type", "OrganizationType"),
        cell: ({ row }) => (
          <div className={"text-sm font-semibold normal-case text-gray-800"}>
            {row.original.organizationType || "-"}
          </div>
        ),
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
      {
        id: "phoneNumber",
        enableSorting: false,
        header: "Phone Number",
        cell: ({ row }) => (
          <div
            className={
              "max-w-[130px] text-sm font-semibold normal-case text-gray-800"
            }>
            {row.original.phoneNumber?.split(",")[0]}
          </div>
        ),
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
      {
        id: "email",
        enableSorting: false,
        header: "Email Address",
        cell: ({ row }) => {
          const email = row.original.email?.split(",")[0];

          return (
            <div
              className={
                "link-primary max-w-[170px] truncate text-sm font-semibold"
              }>
              <ClientEmailLink
                email={email}
                className={"max-w-full truncate text-left hover:underline"}
                onOpen={() =>
                  openCompose({
                    email,
                    customerId: row.original.customerId,
                    name: row.original.customerName,
                    businessContactId: row.original.businessContactId,
                  })
                }
              />
            </div>
          );
        },
        meta: {
          headerClassName:
            "max-w-[170px] px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "max-w-[170px] px-2 py-2",
        },
      },
      {
        id: "contactType",
        enableSorting: false,
        header: () => renderSortableHeader("Contact type", "ContactType"),
        cell: ({ row }) => (
          <div className={"text-sm font-semibold normal-case text-gray-800"}>
            {row.original.customerType}
          </div>
        ),
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
      {
        id: "allWork",
        enableSorting: false,
        header: () => renderSortableHeader("All work", "AllWorks"),
        cell: ({ row }) => (
          <div className={"text-sm font-semibold normal-case text-gray-800"}>
            {row.original.allWorkCounts}
          </div>
        ),
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
      {
        id: "yearEnd",
        enableSorting: false,
        header: () => renderSortableHeader("Year end", "YearEnd"),
        cell: ({ row }) => (
          <div className={"text-sm font-semibold normal-case text-gray-800"}>
            {row.original.yearEndMonth}{" "}
            {row.original.yearEndDay !== 0 && row.original.yearEndDay}
          </div>
        ),
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
      {
        id: "clientOwner",
        enableSorting: false,
        header: "Client owner",
        cell: ({ row }) => (
          <div
            className={
              "max-w-[140px] truncate text-sm font-semibold normal-case text-gray-800"
            }
            title={row.original.clientOwnerName || undefined}>
            {row.original.clientOwnerName || "-"}
          </div>
        ),
        meta: {
          headerClassName:
            "whitespace-nowrap px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
      {
        id: "clientManager",
        enableSorting: false,
        header: "Client manager",
        cell: ({ row }) => (
          <div
            className={
              "max-w-[140px] truncate text-sm font-semibold normal-case text-gray-800"
            }
            title={row.original.clientManagerName || undefined}>
            {row.original.clientManagerName || "-"}
          </div>
        ),
        meta: {
          headerClassName:
            "whitespace-nowrap px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
      {
        id: "tags",
        enableSorting: false,
        header: "Tags",
        cell: ({ row }) => (
          <>
            <div
              className={"cursor-pointer"}
              data-tooltip-id={row.original.customerId}>
              <div
                className={
                  "h-[30px] w-[110px] items-center truncate pt-1.5 text-sm font-semibold normal-case text-gray-800"
                }>
                {row.original.customerTag}
              </div>
            </div>

            <ReactTooltip
              id={row.original.customerId}
              place={"left"}
              positionStrategy={"fixed"}
              content={
                Array.isArray(row.original.customerTag)
                  ? row.original.customerTag.join(", ")
                  : String(row.original.customerTag || "")
              }
              className={"z-100 !rounded-[8px] !p-2 !text-sm"}
            />
          </>
        ),
        meta: {
          headerClassName:
            "px-2 py-4 text-start text-sm normal-case text-gray-400",
          className: "px-2 py-2",
        },
      },
    ],
    [
      allSelected,
      navigate,
      selectedCustomers,
      handleSelectAll,
      handleToggleCustomer,
      renderSortableHeader,
      openCompose,
    ],
  );

  if (isLoadingContacts && !contacts) {
    return <LottieLoading />;
  }

  return (
    <>
      <FormSection name={""} showHeading={false}>
        <div>
          <div className={"text-lg font-bold"}>
            {contactsCountLabel(contacts?.[0]?.totalRecords || 0)}
          </div>
          {/* Phones (QA batch 11): the search box fits the screen width (it
              was 250px + icons and cut off on the right at 390px). */}
          <div
            className={"flex justify-between max-md:!flex-wrap max-md:gap-3"}>
            <div className={"flex gap-2 max-md:w-full max-md:min-w-0"}>
              <div
                className={
                  "input input-bordered flex items-center gap-2 max-md:w-full max-md:min-w-0"
                }>
                <IoSearch size={"20px"} className={"shrink-0"} />
                <input
                  className={
                    "w-[250px] outline-none max-md:w-0 max-md:min-w-0 max-md:flex-1"
                  }
                  placeholder={"Search contacts"}
                  onChange={(e) => setSearch(e.target.value)}
                  value={search}
                />
                {/* Inside the box (it sat outside, next to it). The width is
                    kept so the box doesn't jump when the × appears. */}
                <button
                  type={"button"}
                  aria-label={"Clear search"}
                  className={classNames(
                    "flex h-5 w-5 flex-none cursor-pointer items-center justify-center text-lg leading-none text-gray-400 hover:text-gray-600",
                    !search && "invisible",
                  )}
                  onClick={() => setSearch("")}>
                  ×
                </button>
              </div>
            </div>

            <div className={"flex items-center gap-4"}>
              {!hasSelectedCustomers && (
                <>
                  <div className={"relative"}>
                    <div
                      className={
                        "absolute -top-5 left-0 text-sm font-semibold text-gray-400"
                      }>
                      Tags
                    </div>
                    <div className={"w-[200px]"}>
                      <CustomSelect
                        options={tagOptions}
                        value={filters.tagsValues}
                        onChange={(newValue) => {
                          const value = newValue as SelectOptionType[];
                          setFilters((prev) => ({
                            ...prev,
                            tagsValues: value,
                          }));
                          setPageParam(1);
                        }}
                        placeholder={"All"}
                        inputValue={tagValue}
                        onChangeInput={setTagValue}
                        isMulti
                        withMenu
                        isLoading={isFetchingTags}
                        selectStyle={{ fitChips: true, workTable: true }}
                      />
                    </div>
                  </div>

                  <div className={"relative"}>
                    <div
                      className={
                        "absolute -top-5 left-0 text-sm font-semibold text-gray-400"
                      }>
                      Organization type
                    </div>
                    <div className={"w-[200px]"}>
                      <CustomSelect
                        options={organizationTypeOptions}
                        value={filters.organizationTypes}
                        onChange={(newValue) => {
                          const value = newValue as SelectOptionType[];
                          setFilters((prev) => ({
                            ...prev,
                            organizationTypes: value,
                          }));
                          setPageParam(1);
                        }}
                        placeholder={"All"}
                        inputValue={organizationTypeValue}
                        onChangeInput={setOrganizationTypeValue}
                        isMulti
                        withMenu
                        isLoading={isFetchingOrganizationTypes}
                        selectStyle={{ fitChips: true, workTable: true }}
                      />
                    </div>
                  </div>

                  <div className={"relative"}>
                    <div
                      className={
                        "absolute -top-5 left-0 text-sm font-semibold text-gray-400"
                      }>
                      Year end
                    </div>
                    <div className={"w-[200px]"}>
                      <CustomSelect
                        options={yearEndOptions}
                        value={filters.yearEnd}
                        onChange={(newValue) => {
                          const value = newValue as SelectOptionType[];
                          setFilters((prev) => ({ ...prev, yearEnd: value }));
                          setPageParam(1);
                        }}
                        placeholder={"All"}
                        inputValue={yearEndValue}
                        onChangeInput={setYearEndValue}
                        isMulti
                        withMenu
                        isLoading={isFetchingTags}
                        selectStyle={{ fitChips: true, workTable: true }}
                      />
                    </div>
                  </div>

                  {(
                    [
                      ["Client owner", teamOwners, setTeamOwners],
                      ["Client manager", teamManagers, setTeamManagers],
                    ] as const
                  ).map(([label, values, setValues], index) => (
                    <div className={"relative"} key={label}>
                      <div
                        className={
                          "absolute -top-6 left-0 flex items-center gap-0.5 whitespace-nowrap text-sm font-semibold text-gray-400"
                        }>
                        {label}
                        {index === 0 && <ClientTeamInfoButton />}
                      </div>
                      <div className={"w-[160px]"}>
                        <CustomSelect
                          options={peopleOptions}
                          value={optionsFor(values)}
                          onChange={(newValue) => {
                            const picked = (newValue as SelectOptionType[]).map(
                              (o) => o.value,
                            );
                            setValues(picked);
                            setPageParam(1);
                          }}
                          placeholder={"All"}
                          isMulti
                          withMenu
                          selectStyle={{ fitChips: true, workTable: true }}
                        />
                      </div>
                    </div>
                  ))}

                  <Button
                    type={"button"}
                    variant={"outline"}
                    size={"md"}
                    width={100}
                    disabled={isImporting}
                    leftIcon={<LuUpload size={16} strokeWidth={1.9} />}
                    onClick={() => setIsOpenImport(true)}>
                    Import
                  </Button>

                  <Button
                    type={"button"}
                    variant={"primary"}
                    size={"md"}
                    width={140}
                    leftIcon={<LuPlus size={17} strokeWidth={2.4} />}
                    onClick={() => setIsOpen(true)}>
                    Add contact
                  </Button>
                </>
              )}

              {hasSelectedCustomers && (
                <>
                  <span
                    className={
                      "mr-1 whitespace-nowrap text-sm font-bold text-[#6B4EFF]"
                    }>
                    {selectedCustomersLabel}
                  </span>

                  <Button
                    type={"button"}
                    variant={"outline"}
                    size={"md"}
                    width={130}
                    leftIcon={<LuBriefcase size={16} strokeWidth={1.9} />}
                    onClick={() => setIsAddWorkOpen(true)}>
                    Create work
                  </Button>

                  <Button
                    type={"button"}
                    variant={"outline"}
                    size={"md"}
                    width={100}
                    leftIcon={<LuArchive size={16} strokeWidth={1.9} />}
                    onClick={() => setIsBulkArchiveConfirmOpen(true)}>
                    Archive
                  </Button>

                  <Button
                    type={"button"}
                    variant={"outline"}
                    size={"md"}
                    width={160}
                    leftIcon={<LuUsers size={16} strokeWidth={1.9} />}
                    onClick={() => setIsBulkTeamOpen(true)}>
                    Set client team
                  </Button>

                  <Button
                    type={"button"}
                    variant={"outline"}
                    size={"md"}
                    width={110}
                    leftIcon={<LuTags size={16} strokeWidth={1.9} />}
                    onClick={() => setAddTag(true)}>
                    Add tags
                  </Button>

                  <Button
                    type={"button"}
                    variant={"outline"}
                    size={"md"}
                    width={100}
                    leftIcon={<LuUpload size={16} strokeWidth={1.9} />}
                    onClick={() => exportDataToExcel(exportData, "Contacts")}>
                    Export
                  </Button>

                  <Button
                    type={"button"}
                    variant={"primary"}
                    size={"md"}
                    width={95}
                    leftIcon={<LuMail size={16} strokeWidth={1.9} />}
                    onClick={() => setIsOpenCompose(true)}>
                    Email
                  </Button>

                  <Button
                    type={"button"}
                    variant={"ghost"}
                    size={"md"}
                    iconOnly
                    title={"Clear selection"}
                    className={"text-[#6B7185] hover:bg-[#F1F2F5]"}
                    leftIcon={<LuX size={16} strokeWidth={2.2} />}
                    onClick={() => setSelectedCustomers([])}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <AddContactModal
          isOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          isSearch={false}
        />

        {isAddWorkOpen && (
          <AddWorkModal
            isOpen={isAddWorkOpen}
            onClose={() => setIsAddWorkOpen(false)}
            defaultContacts={selectedCustomerOptions}
            hideContactSelect
          />
        )}

        {isLoadingContacts ? (
          <LottieLoading />
        ) : (
          <Table
            columns={contactColumns}
            data={contacts || []}
            getRowId={(contact) => contact.customerId || contact.id || ""}
            tableClassName={"w-full"}
            theadClassName={"border-b-[1px] border-gray-300"}
            rowClassName={"border-b-[1px] border-gray-300 hover:bg-gray-100"}
          />
        )}

        {!!contacts?.length && (
          <ListPager
            page={page}
            pageSize={20}
            total={contacts?.[0]?.totalRecords || 0}
            disabled={isLoadingContacts}
            onChange={setPageParam}
          />
        )}
      </FormSection>

      {isOpenCompose && (
        <ComposeEmailModal
          isActive={isOpenCompose}
          onClose={() => setIsOpenCompose(false)}
          bulk={true}
          customersEmail={selectedCustomers
            .filter((customer) => customer.email)
            .map((customer) => ({ email: customer.email }))}
        />
      )}

      {composeModal}

      {isBulkTeamOpen && (
        <BulkClientTeamModal
          customerIds={selectedCustomers.map((c) => c.customerId)}
          onClose={() => setIsBulkTeamOpen(false)}
        />
      )}

      {isOpenImport && (
        <ImportContacts
          handleImport={handleImport}
          isOpen={isOpenImport}
          closeModal={() => setIsOpenImport(false)}
        />
      )}

      {pendingImport && (
        <SettingsConfirmModal
          onClose={handleCancelImport}
          onConfirm={handleConfirmImport}
          isLoading={isImporting}
          icon={<PiFileArrowUp size={32} />}
          title={`Continue uploading "${pendingImport.file.name}"?`}
          description={
            "Are you sure you want to upload this file?<br />Once the import starts, you won't be able to cancel it."
          }
          confirmText={"Continue"}
          variant={"primary"}
        />
      )}

      {isImportSuccessful && (
        <SettingsConfirmModal
          onClose={handleCloseImportSuccess}
          onConfirm={handleCloseImportSuccess}
          icon={
            <div className={"relative flex items-center justify-center"}>
              <PiFileArrowUp size={32} />
              <IoCheckmarkCircle
                size={20}
                className={"absolute -bottom-2 -right-4 text-[#10B981]"}
              />
            </div>
          }
          title={importResult?.title || "Import finished"}
          description={importResult?.description || ""}
          confirmText={"OK"}
          variant={"primary"}
          iconVariant={importResult?.ok === false ? "warning" : "success"}
          noCancel
        />
      )}

      {isBulkArchiveConfirmOpen && (
        <SettingsConfirmModal
          onClose={() => setIsBulkArchiveConfirmOpen(false)}
          onConfirm={handleArchiveSelectedContacts}
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={"Archive selected Contacts?"}
          description={`Are you sure you want to archive ${
            selectedCustomers.length
          } selected contact${selectedCustomers.length === 1 ? "" : "s"}?`}
          confirmText={"Archive"}
          variant={"warning"}
          isLoading={isArchivingContacts}
        />
      )}

      {addTag && (
        <div className={`modal ${addTag ? "modal-open" : "modal-close"}`}>
          <div
            className={
              "modal-box flex h-[500px] w-[550px] flex-wrap rounded-md bg-white px-3 py-2"
            }>
            <div className={"h-[60px] w-full pb-2"}>
              <CustomSelect
                options={tagOptions}
                value={tagsValue}
                onChange={(newValue) => {
                  const value = newValue as SelectOptionType[];
                  setTagsValue(value);
                }}
                inputValue={tagValue}
                onChangeInput={setTagValue}
                isMulti
                isLoading={isFetchingTags}
                autoFocus
                selectStyle={{
                  fitChips: true,
                  fullWidth: true,
                  height: "h-[170px]",
                }}
              />
            </div>

            <div
              className={
                "mt-4 flex h-[300px] w-full flex-wrap gap-0 overflow-y-auto px-2"
              }>
              <div className={"h-fit w-full"}>
                {tagsValue.map((item) => (
                  <div
                    key={String(item.value)}
                    className={
                      "flex h-[30px] w-[100%] items-center justify-between border-b border-gray-200 px-2 py-2"
                    }>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>

            <div className={"text-base font-semibold"}>
              This tags will be added to the selected clients
            </div>

            <div className={"flex h-[10%] w-full items-end justify-end gap-2"}>
              <LegacyButton
                label={"Add"}
                extraClasses={"min-h-[40px] h-[40px] flex items-center !py-2"}
                onClick={() => {
                  addMultipleTags({
                    tagIds: tagsValue.map((item) => item.value),
                    customerIds: selectedCustomers.map(
                      (item) => item.customerId,
                    ),
                  });
                  setAddTag(false);
                  setSelectedCustomers([]);
                }}
              />

              <LegacyButton
                label={"Cancel"}
                colorType={"outline"}
                customSize={"px-1 py-1"}
                extraClasses={"min-h-[40px] h-[40px] flex items-center !py-2"}
                onClick={() => {
                  setAddTag(false);
                  setTagsValue([]);
                  setSelectedCustomers([]);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
